import { Capacitor } from "@capacitor/core";

/**
 * Platform detection for App Store compliance.
 *
 * Apple's App Store guidelines (3.1.1) historically required all in-app
 * purchases of digital content/services to use Apple's IAP (with the 30%/15%
 * cut). Post the Epic ruling + EU DMA, an app can now route users to the
 * web for subscription purchase, but it must NOT have a Stripe-checkout-in-
 * webview flow inside the iOS app — that triggers rejection.
 *
 * Shop Ledger's chosen posture (Option A): on iOS, hide all "Subscribe"
 * UI and direct the user to shopledger.app on a web browser to subscribe.
 * The web checkout updates Supabase, and the iOS app reads sub status
 * from there — so users who subscribe via web automatically get full
 * access in the iOS app on next session refresh.
 *
 * isNativeIOS() — true when running inside Capacitor's native iOS shell.
 * Does NOT match iOS Safari (which is fine — web browser users can
 * subscribe normally, the App Store rules only apply inside the app).
 */
export function isNativeIOS(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
  } catch {
    return false;
  }
}

/**
 * isNativeAndroid — Google Play has analogous rules but they're less
 * strict for B2B SaaS. We still hide subscription UI on Android for
 * symmetry; revisit if conversion is hurting.
 */
export function isNativeAndroid(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
  } catch {
    return false;
  }
}

/**
 * isNativeApp — true for any Capacitor native shell (iOS or Android).
 * Use when the rule applies to either platform.
 */
export function isNativeApp(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/**
 * Open a URL in the system browser (NOT the in-app webview). On native
 * Capacitor this uses a plain window.open which Capacitor's default URL
 * handling routes to the OS browser for any external https:// URL.
 *
 * Importantly: when redirecting users to subscribe, we direct them to
 * shopledger.app/settings — NOT to a direct Stripe checkout URL.
 * Apple's rule is "no buy buttons that route to web payment"; routing
 * to your own marketing/account page is fine.
 */
export function openInBrowser(url: string): void {
  // Capacitor 8 routes external https:// URLs to the system browser by
  // default. window.open with _blank is the canonical way; the @capacitor/
  // browser plugin would offer a nicer in-app SafariViewController, but
  // we WANT the full system browser to keep the subscribe flow clearly
  // outside the app for App Review.
  window.open(url, "_blank");
}
